import React from 'react';
import { LoginHistoryEntry } from '../../types/audit';
import { EmptyState } from '../ui/Feedback';
import { CheckCircle2, XCircle } from 'lucide-react';

interface LoginHistoryTableProps {
  history: LoginHistoryEntry[];
  isLoading?: boolean;
}

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

export const LoginHistoryTable: React.FC<LoginHistoryTableProps> = ({ history, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-11 bg-slate-800/50 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return <EmptyState message="No login history" description="Login attempts will appear here." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-800">
            <th className="text-left py-3 px-3 font-semibold text-slate-400">Time</th>
            <th className="text-left py-3 px-3 font-semibold text-slate-400">User</th>
            <th className="text-left py-3 px-3 font-semibold text-slate-400">IP Address</th>
            <th className="text-left py-3 px-3 font-semibold text-slate-400">Status</th>
            <th className="text-left py-3 px-3 font-semibold text-slate-400">Reason</th>
          </tr>
        </thead>
        <tbody>
          {history.map(entry => (
            <tr key={entry.log_id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
              <td className="py-3 px-3 text-slate-400 whitespace-nowrap">{formatDateTime(entry.timestamp)}</td>
              <td className="py-3 px-3">
                <div className="text-slate-200">{entry.user_name}</div>
                <div className="text-[10px] text-slate-500">{entry.email}</div>
              </td>
              <td className="py-3 px-3 text-slate-400 font-mono">{entry.ip_address}</td>
              <td className="py-3 px-3">
                {entry.status === 'SUCCESS' ? (
                  <span className="inline-flex items-center gap-1 text-emerald-400">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Success
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-400">
                    <XCircle className="w-3.5 h-3.5" /> Failed
                  </span>
                )}
              </td>
              <td className="py-3 px-3 text-slate-400">{entry.failure_reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
